import { api } from '@/helpers/apiClient'
import { furnitureUsageDtoSchema, type FurnitureUsageDto } from '@lib/dto/plan-impact.dto'
import { logRequestFailure } from '@/helpers/request-outcome'

import { furnitureItemDtoSchema, furnitureItemListDtoSchema, type FurnitureItemDto, type FurnitureItemListDto } from '@lib/dto/furniture-item.dto'
import { furnitureGroupDtoSchema, furnitureGroupListDtoSchema, type FurnitureGroupDto, type FurnitureGroupListDto } from '@lib/dto/furniture-group.dto'
import type { CreateVendorFurnitureItemInput, UpdateFurnitureItemInput } from '@lib/input/furniture-item.input'
import type { CreateVendorFurnitureGroupInput, UpdateFurnitureGroupInput } from '@lib/input/furniture-group.input'

/**
 * A vendor user's own furniture library and groups (the `/vendor/furniture*`
 * endpoints). Responses are validated against the shared `@lib` schemas; the
 * vendor is inferred from the token by the API.
 */
class VendorFurnitureService {
  async listItems(): Promise<FurnitureItemListDto> {
    try {
      return await api.get('/vendor/furniture', furnitureItemListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to list vendor furniture', {}, error)
      throw error
    }
  }

  async createItem(input: CreateVendorFurnitureItemInput): Promise<FurnitureItemDto> {
    try {
      return await api.post('/vendor/furniture', input, furnitureItemDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to create vendor furniture', {}, error)
      throw error
    }
  }

  /** Where one of the vendor's pieces is rented, by venue and day. */
  async itemUsage(id: string): Promise<FurnitureUsageDto> {
    try {
      return await api.get(`/vendor/furniture/${id}/usage`, furnitureUsageDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load where a vendor piece is rented', { id }, error)
      throw error
    }
  }

  async updateItem(id: string, input: UpdateFurnitureItemInput): Promise<FurnitureItemDto> {
    try {
      return await api.patch(`/vendor/furniture/${id}`, input, furnitureItemDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to update vendor furniture', { id }, error)
      throw error
    }
  }

  async listGroups(): Promise<FurnitureGroupListDto> {
    try {
      return await api.get('/vendor/furniture-groups', furnitureGroupListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to list vendor furniture groups', {}, error)
      throw error
    }
  }

  async createGroup(input: CreateVendorFurnitureGroupInput): Promise<FurnitureGroupDto> {
    try {
      return await api.post('/vendor/furniture-groups', input, furnitureGroupDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to create vendor furniture group', {}, error)
      throw error
    }
  }

  async updateGroup(id: string, input: UpdateFurnitureGroupInput): Promise<FurnitureGroupDto> {
    try {
      return await api.patch(`/vendor/furniture-groups/${id}`, input, furnitureGroupDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to update vendor furniture group', { id }, error)
      throw error
    }
  }
}

export const vendorFurnitureService = new VendorFurnitureService()
